import { INITIAL_ID } from '@components/CreditRequest/type'
import notificationService from '@components/Notification/realtime-notification/realtimeNotificationService'
import { ActivityType } from '@components/Notification/realtime-notification/types'
import { AustraliaTimeZones } from '@consts/timezones'
import { ApplicationInsights } from '@microsoft/applicationinsights-web'
import * as Sentry from '@sentry/browser'
import { ApiService } from '@services/ApiService'
import {
  initSessionConfig,
  startSessionExpiryWatcher,
} from '@services/baseService'
import localAccountManager from '@services/localAccountManager'
import * as authService from '@services/oauthService'
import { logout } from '@services/oauthService'
import LocalStorageManager from '@services/storage/LocalStorageManager'
import {
  AssistantUnavailableReason,
  getAssistantUnavailableReason,
} from '@stores/assistantAccess'
import { isMaintenanceBlocking } from '@stores/maintenanceAccess'
import {
  BUSINESS_TYPE,
  CONTRACTOR_TYPE,
  FROM_MODULE_TYPE,
  ROLE_NAME,
  TIMEZONE_LIST,
} from '@utils/constants'
import { showNotification } from '@utils/errors'
import { getResetPasswordConfirmMessage } from '@utils/loginStatus'
import {
  getCurrencySymbol,
  initFavicon,
  numberWithCommas,
  showNotificationWarning,
} from '@utils/functions'
import {
  getSessionColourScheme,
  RESOLVED_SESSION_COLOUR_SCHEME_KEY,
  SESSION_COLOUR_SCHEME,
  visibleFilterOptions,
} from '@utils/sessionColourSchemes'
import dayjs from 'dayjs'
import { action, computed, makeAutoObservable } from 'mobx'
import moment, { Moment } from 'moment-timezone'

class AppStore {
  constructor() {
    makeAutoObservable(this, {
      defaultSenderEmail: computed,
      tenantCurrency: computed,
      currencySymbol: computed,
    })
  }

  appInsights: ApplicationInsights = null
  //#region Props
  states = []

  ifOffshoreStaff() {
    // Offshore rules apply to both staff-side roles — STAFF and ADMIN_STAFF — mirroring the
    // backend rule in CaseDetailManager.IsOffshoreStaffAsync. An offshore Admin Staff keeps the
    // full admin menu but inherits every data-masking / attachment / correspondence restriction.
    // A user can hold several roles: ADMIN is the tenant's highest authority and outranks the
    // rule, so an account holding it is never offshore even alongside ADMIN_STAFF.
    return (
      this.currentUser?.isOffshoreStaff &&
      this.features.OffshoreStaffAccessControl &&
      (appStore.isStaff || appStore.isAdminStaff) &&
      !appStore.isTenantAdmin
    )
  }
  currentUser = {
    id: null,
    roles: [] as string[],
    dateFormat: 'DD MMM YYYY',
    caseManager: {} as any,
    contractor: {} as any,
    specialist: {} as any,
    email: null,
    displayName: null,
    extraProperties: null as any,
    firstName: null,
    lastName: null,
    fullName: null,
    name: null,
    isActive: null,
    userName: null,
    surname: null,
    subscription: null as any,
    tenantId: null,
    title: null,
    company: null,
    contractorCompany: null,
    caseManagerCompany: null,
    settings: {} as any,
    twoFactorEnabled: false,
    isOffshoreStaff: false,
  }
  quickSetting = false
  isOpenChatbot = false
  authenticated = false
  isImpersonated = false
  loaded = false
  isTenantAdmin = false
  isAdminStaff = false
  isCaseManager = false
  isSpecialist = false
  isStaff = false
  isContractor = false
  isAccounting = false
  isMagStaff = false
  isInsurerContractor = false
  isBrokerContractor = false
  isAdminOrStaff = false
  isAdminOrAccounting = false
  isAdminOrStaffOrCM = false
  isAdminOrStaffOrSpecialist = false
  labelOperator = 'MLP'
  currentTenant = {
    id: null,
    name: null,
    features: {} as any,
    country: {} as any,
    editionName: null,
    extraProperties: {} as any,
    settings: {} as any,
    timezone: null,
    defaultCountryId: null,
    defaultPicId: null,
    defaultPic: null,
  }

  /**
   * List of disabled email template keys
   */
  disabledTemplateKeys: string[] = []

  /**
   * Email templates an offshore staff member may see. Empty means offshore staff get no
   * correspondence at all — see `offshoreTemplateAccess`. Only consulted for offshore users.
   */
  offshoreVisibleTemplateKeys: string[] = []
  hostTenant = {
    id: null,
    name: null,
    features: null as any,
    country: {} as any,
    editionName: null,
    extraProperties: {} as any,
    settings: {} as any,
    timezone: null,
    defaultCountryId: null,
    defaultPicId: null,
    defaultPic: null,
  }
  timezone = ''
  features = {
    Assessment: false,
    MedNeg: false,
    COTELE: false,
    FileReview: false,
    Supplementary: false,
    MedicalRecord: false,
    MarketingCampaign: false,
    TreatmentProvider: false,
    MassTorts: false,
    OT: false,
    Disbursement: false,
    DisbursementFunding: false,
    InsuranceClaim: false,
    FundingTransaction: false,
    NDIS: false,
    TimeTracking: false,
    InquiryCentre: false,
    LegalPracticeAPI: false,
    CaseManagement: false,
    MVA: false,
    LMQ: false,
    ICareClaim: false,
    MagExtraResources: false,
    AdHocTemplate: false,
    CompanyPMSConfig: false,
    QaStatus: false,
    ServiceBudget: false,
    RealtimeNotification: false,
    TPD: false,
    ImpairmentChecker: false,
    BookingAgent: false,
    DictationRecording: false,
    MatterTypeFiltering: false,
    EMLIntegration: false,
    PayingSpecialist: false,
    SpecialistAgreementTemplate: false,
    ReportQASheet: false,
    AttachmentRedaction: false,
    OffshoreStaffAccessControl: false,
    EntraIdLogin: false,
    MultipleShareLinks: false,
    MultiXeroConnection: false,
    AccountCodeSetting: false,
    AIService: false,
    AIAssistant: false,
    AIAssistantCaseManager: false,
    AIAssistantSpecialist: false,
    AIDocumentSummary: false,
    AIPaperworkPreCheck: false,
    AIPaperworkQACheck: false,
    AIReportQACheck: false,
    AIBookingAssistant: false,
    CompanyEmailCustomization: false,
    AutoSendingServicePromotionNewsletter: false,
    AvailabilityCampaignExport: false,
  }
  edition = {
    MLP: false,
    LAW_CONNECT: false,
    FUNDING_PROVIDER: false,
    LMQ_PROVIDER: false,
    MARKETING: false,
  }
  settings = {}

  /**
   * Which session colour scheme this user is on. The server decides it from the tenant's rollout
   * stage and the user's roles and sends it with the per-user settings, so staff and clients can be
   * on different schemes at the same moment. Legacy until the server says otherwise.
   */
  sessionColourSchemeId: string = SESSION_COLOUR_SCHEME.LEGACY_V1

  /**
   * Set when the page shows sessions on behalf of a case manager without that case manager being
   * signed in to Kawaconn — the LEAP host connection. The session colours and the case manager
   * rules then apply as if they were.
   */
  sessionViewerActsAsCaseManager = false

  isOpenConfirmModal = false
  isOpenWarningWithoutSave = false
  warningWithoutSaveParams: any = {}
  confirmModalParams = {}
  isOpenPDFViewer = false
  pdfViewerParams = { fileId: '', fileName: '' }
  showThemeSetting = false
  theme = localStorage.getItem('theme') || 'dark'

  enableHint = localStorage.getItem('ENABLE_HINT') === 'true' ? true : false
  logoColor = localStorage.getItem('LOGO_COLOR')
    ? JSON.parse(localStorage.getItem('LOGO_COLOR'))
    : null

  get tenantCurrency() {
    return this.currentTenant?.settings['ICS.Currency'] || 'USD'
  }

  /**
   * The session colour scheme in force for this user — the single place the calendar, the list view
   * and the calendar key read their colours and wording from.
   */
  get sessionColourScheme() {
    return getSessionColourScheme(this.sessionColourSchemeId)
  }

  /**
   * The availability filter values to show this user. A case manager is never sent an unavailable
   * session, so the simplified scheme leaves that value out for them.
   */
  get sessionFilterOptions() {
    return visibleFilterOptions(this.sessionColourScheme, {
      isCaseManager: this.sessionViewerIsCaseManager,
    })
  }

  /** Whether sessions are being shown to a case manager, signed in or acting through LEAP. */
  get sessionViewerIsCaseManager() {
    return this.isCaseManager || this.sessionViewerActsAsCaseManager
  }

  /**
   * The one place the session colour scheme is set. Every entry point that learns which scheme is
   * in force — signing in to Kawaconn, or the LEAP panel picking a tenant — goes through here, so
   * the calendar, the list view, the calendar key and the LEAP search all read the same value.
   * Anything unrecognised or missing falls back to the legacy scheme.
   */
  applySessionColourScheme = (
    schemeId: string | null | undefined,
    options: { actsAsCaseManager?: boolean } = {}
  ) => {
    this.sessionColourSchemeId = getSessionColourScheme(
      schemeId ?? undefined
    ).id
    if (options.actsAsCaseManager !== undefined) {
      this.sessionViewerActsAsCaseManager = options.actsAsCaseManager
    }
  }

  get currencySymbol() {
    return getCurrencySymbol(this.tenantCurrency)
  }

  get sendToXero() {
    return (
      this.currentTenant?.settings['ICS.Xero.SendToXero']
        ?.toString()
        .toLowerCase() === 'true'
    )
  }

  /**
   * Maintenance Mode — resolved by the server (global OR tenant) and delivered with the tenant
   * settings right after sign-in. Host users never see this: the server exempts them so a master
   * admin can always reach the screen that turns maintenance off.
   */
  get maintenance() {
    const settings = this.currentTenant?.settings ?? {}
    return {
      enabled:
        settings['ICS.Maintenance.Enabled']?.toString().toLowerCase() ===
        'true',
      allowTenantAdmin:
        settings['ICS.Maintenance.AllowTenantAdmin']
          ?.toString()
          .toLowerCase() === 'true',
      title: settings['ICS.Maintenance.Title'] ?? '',
      message: settings['ICS.Maintenance.Message'] ?? '',
    }
  }

  /**
   * True when the application must be replaced by the maintenance notice.
   *
   * The server already applies the exemptions (host users, and the tenant's own ADMIN when the
   * master admin ticked "allow tenant admin"). The ADMIN rule is repeated here on purpose: an
   * admin who was deliberately let through the sign-in guard must never be stopped by the SPA,
   * and this keeps that true even against an API that still reports the tenant-wide state.
   */
  get isUnderMaintenance() {
    return isMaintenanceBlocking({
      enabled: this.maintenance.enabled,
      allowTenantAdmin: this.maintenance.allowTenantAdmin,
      isTenantAdmin: this.isTenantAdmin,
    })
  }

  get sendToQuickBook() {
    return (
      this.currentTenant?.settings['ICS.QuickBook.SendToQuickBook']
        ?.toString()
        .toLowerCase() === 'true'
    )
  }

  get allowCancelCase() {
    if (!this.isCaseManager) {
      return true
    }
    return (
      this.currentTenant?.settings[
        'ICS.BookingCancellationSetting.PreventCMCancelBooking'
      ]?.toLowerCase() !== 'true'
    )
  }

  formatMoney = (value, currency = null) => {
    if (value == null || isNaN(value)) {
      value = 0
    }
    return `${getCurrencySymbol(
      currency || this.tenantCurrency
    )} ${numberWithCommas(value, 2)}`
  }

  get postCodeLabel() {
    return this.currentTenant.country?.name === 'United States'
      ? 'Zipcode'
      : 'Postcode'
  }
  get isRingCentralEmbeddable() {
    return (
      this.currentTenant?.settings[
        'ICS.RingCentral.EnableRingCentral'
      ]?.toLowerCase() == 'true'
    )
  }

  get isEnableIROFunding() {
    return (
      this.currentTenant?.settings[
        'ICS.Feature.EnableIROFunding'
      ]?.toLowerCase() == 'true'
    )
  }

  get isCountryUS() {
    return this.currentTenant.country?.name === 'United States'
  }

  get isHostTenant() {
    return this.currentTenant?.id == INITIAL_ID
  }

  get isFundingProviderInsurerCM() {
    return this.edition.FUNDING_PROVIDER && this.isCaseManager
  }

  get isLawConnectOrCM() {
    return this.edition.LAW_CONNECT || this.isCaseManager
  }

  get isDemoAccount() {
    return this.currentTenant.name === 'MLP Demo' && !this.isAdminStaff
  }

  /**
   * An AI feature runs only with its own switch AND the AI Service master
   * switch on, and only for Staff and Admin Staff — the same rule as the
   * backend's AIFeatureGate. Every other role never gets AI features. The AI
   * Assistant is not one of them: it has its own audience — isAssistantEnabled.
   */
  isAIFeatureEnabled(
    feature:
      | 'AIDocumentSummary'
      | 'AIPaperworkPreCheck'
      | 'AIPaperworkQACheck'
      | 'AIReportQACheck'
      | 'AIBookingAssistant'
  ) {
    return (
      this.canUseAIFeatures &&
      !!this.features.AIService &&
      !!this.features[feature]
    )
  }

  /** AI features are switched on for Staff and Admin Staff only. */
  get canUseAIFeatures() {
    return this.isStaff || this.isAdminStaff
  }

  /**
   * Why the AI Assistant is not available to this user (the backend's own
   * codes), or null when it is. The assistant's own audience rule, mirroring
   * the backend's AssistantAudience: Staff, Admin Staff and the tenant Admin
   * with AIAssistant on, a Case Manager or Specialist only when the host also
   * opened the assistant to that audience for the tenant, nobody else.
   */
  get assistantUnavailableReason(): AssistantUnavailableReason | null {
    return getAssistantUnavailableReason({
      roles: this.currentUser?.roles,
      features: this.features,
    })
  }

  /** Whether the AI Assistant launcher, drawer and page are shown to this user. */
  get isAssistantEnabled() {
    return this.assistantUnavailableReason === null
  }

  get isMAGTenant() {
    return this.currentTenant?.name == 'MAG'
  }

  get isVMLSTenant() {
    return this.currentTenant?.name == 'VMLS'
  }

  get colorLogo() {
    if (this.logoColor) {
      return this.logoColor
    }
    const tenantProperties = this.isHostTenant
      ? this.hostTenant?.extraProperties
      : this.currentTenant?.extraProperties
    return {
      LogoBackgroundColor: tenantProperties?.LogoBackgroundColor || 'none',
      LoginLogoBackgroundColor:
        tenantProperties?.LoginLogoBackgroundColor || 'none',
    }
  }

  get defaultSenderEmail() {
    return appStore.currentTenant.settings['ICS.Office365.Enable']
      ? appStore.currentTenant.settings['ICS.Office365.SenderEmail']
      : appStore.currentTenant.settings['ICS.SendGrid.SenderEmail']
  }

  //#region Application Insight Tracking
  // VITE_ENABLE_TRACKING=false turns client-side Application Insights off: no telemetry request is sent
  // Block body on purpose: Vite's `define` inlines `process.env` as an object
  // literal, and `=> {...}` would be parsed as a block and break the build
  isTrackingEnabled = () => {
    return process.env.VITE_ENABLE_TRACKING === 'true' && !!this.appInsights
  }

  trackPageView = (pageName: string, uri: string) => {
    if (!this.isTrackingEnabled()) {
      return
    }
    this.appInsights.trackPageView({
      name: pageName,
      uri: uri,
      properties: {
        tenantId: this.currentTenant?.id,
        userId: this.currentUser?.id,
        tenantName: this.currentTenant?.name,
      },
    })
  }
  startTrackPage = (pageName: string) => {
    if (!this.isTrackingEnabled()) {
      return
    }
    this.appInsights.startTrackPage(pageName)
  }

  stopTrackPage = (pageName: string) => {
    if (!this.isTrackingEnabled()) {
      return
    }
    const currentUrl = document.location.href
    this.appInsights.stopTrackPage(pageName, currentUrl, {
      tenantId: this.currentTenant?.id,
      userId: this.currentUser?.id,
      tenantName: this.currentTenant?.name,
    })
  }
  //#endregion
  setEnableHint = (enable: boolean) => {
    localStorage.setItem('ENABLE_HINT', enable ? 'true' : 'false')
    this.enableHint = enable
  }

  setTimeZone = timeZone => {
    this.timezone = timeZone
    window.APP_CONFIG.timeZone = timeZone
  }
  async init() {
    const user = await this.getCurrentUser()
    if (!user) {
      return
    }

    let currentTenant
    try {
      currentTenant = await ApiService.Tenant.GetById('myTenantSetting')
      initSessionConfig(currentTenant?.data?.settings ?? {})
      startSessionExpiryWatcher()
    } catch {
      localAccountManager.removeCurrentAccount()
      logout()
    }

    if (this.isTrackingEnabled()) {
      this.appInsights.setAuthenticatedUserContext(
        `userId:${user.id}_tenant:${currentTenant?.data?.id}`
      )
    }

    action(() => {
      if (currentTenant?.data) {
        this.currentTenant = currentTenant.data
        this.validateTenantAndDomain()
        this.updateCurrentLoginAccount()

        initFavicon(this.currentTenant.extraProperties?.Favicon)
        this.features = {
          Assessment:
            this.currentTenant.features['Application:Assessment'] === 'True',
          Supplementary:
            this.currentTenant.features['Application:Supplementary'] === 'True',
          FileReview:
            this.currentTenant.features['Application:FileReview'] === 'True',
          MedicalRecord:
            this.currentTenant.features['Application:MedicalRecord'] === 'True',
          MedNeg: this.currentTenant.features['Application:MedNeg'] === 'True',
          COTELE: this.currentTenant.features['Application:COTELE'] === 'True',
          MarketingCampaign:
            this.currentTenant.features['Application:MarketingCampaign'] ===
            'True',
          TreatmentProvider:
            this.currentTenant.features['Application:TreatmentProvider'] ===
            'True',
          MassTorts:
            this.currentTenant.features['Application:MassTorts'] === 'True',
          OT: this.currentTenant.features['Application:OT'] === 'True',
          Disbursement:
            this.currentTenant.features['Application:Disbursement'] === 'True',
          DisbursementFunding:
            this.currentTenant.features['Application:DisbursementFunding'] ===
            'True',
          InsuranceClaim:
            this.currentTenant.features['Application:InsuranceClaim'] ===
            'True',
          FundingTransaction:
            this.currentTenant.features['Application:FundingTransaction'] ===
            'True',
          NDIS: this.currentTenant.features['Application:NDIS'] === 'True',
          TimeTracking:
            this.currentTenant.features['Application:TimeTracking'] === 'True',
          InquiryCentre:
            this.currentTenant.features['Application:InquiryCentre'] === 'True',
          LegalPracticeAPI:
            this.currentTenant.features['Application:LegalPracticeAPI'] ===
            'True',
          CaseManagement: false,
          MVA: this.currentTenant.features['Application:MVA'] === 'True',
          LMQ: this.currentTenant.features['Application:LMQ'] === 'True',
          ICareClaim:
            this.currentTenant.features['Application:ICareClaim'] === 'True',
          MagExtraResources:
            this.currentTenant.features['Application:MagExtraResources'] ===
            'True',
          AdHocTemplate:
            this.currentTenant.features['Application:AdHocTemplate'] === 'True',
          CompanyPMSConfig:
            this.currentTenant.features['Application:CompanyPMSConfig'] ===
              'True' &&
            (appStore.isAdminOrStaff ||
              appStore.currentUser?.caseManagerCompany?.defaultPMSCompanyId),
          QaStatus:
            this.currentTenant.features['Application:QaStatus'] === 'True',
          ServiceBudget:
            this.currentTenant.features['Application:ServiceBudget'] === 'True',
          RealtimeNotification:
            this.currentTenant.features['Application:RealtimeNotification'] ===
            'True',
          TPD: this.currentTenant.features['Application:TPD'] === 'True',
          ImpairmentChecker:
            this.currentTenant.features['Application:ImpairmentChecker'] ===
            'True',
          BookingAgent:
            this.currentTenant.features['Application:BookingAgent'] === 'True',
          DictationRecording:
            this.currentTenant.features['Application:DictationRecording'] ===
            'True',
          MatterTypeFiltering:
            this.currentTenant.features['Application:MatterTypeFiltering'] ===
            'True',
          EMLIntegration:
            this.currentTenant.features[
              'Application:EMLIntegration'
            ]?.toLowerCase() === 'true',
          PayingSpecialist:
            this.currentTenant?.settings[
              'ICS.Feature.PayingSpecialist'
            ]?.toLowerCase() == 'true',

          SpecialistAgreementTemplate:
            this.currentTenant.features[
              'Application:SpecialistAgreementTemplate'
            ]?.toLowerCase() == 'true',

          ReportQASheet:
            this.currentTenant.features[
              'Application:ReportQASheet'
            ]?.toLowerCase() === 'true',
          AttachmentRedaction:
            this.currentTenant.features[
              'Application:AttachmentRedaction'
            ]?.toLowerCase() === 'true',
          OffshoreStaffAccessControl:
            this.currentTenant.features[
              'Application:OffshoreStaffAccessControl'
            ]?.toLowerCase() === 'true',
          EntraIdLogin:
            this.currentTenant.features[
              'Application:EntraIdLogin'
            ]?.toLowerCase() === 'true',
          MultipleShareLinks:
            this.currentTenant.features[
              'Application:MultipleShareLinks'
            ]?.toLowerCase() === 'true',
          MultiXeroConnection:
            this.currentTenant.features[
              'Application:MultiXeroConnection'
            ]?.toLowerCase() === 'true',
          AccountCodeSetting:
            this.currentTenant.features[
              'Application:AccountCodeSetting'
            ]?.toLowerCase() === 'true',
          // AI Service master switch (default ON on the backend). Gates all
          // Foundry/OpenAI-backed AI UI: document summary, chatbot, doc search, LOI/QA.
          AIService:
            this.currentTenant.features[
              'Application:AIService'
            ]?.toLowerCase() === 'true',
          AIAssistant:
            this.currentTenant.features[
              'Application:AIAssistant'
            ]?.toLowerCase() === 'true',
          // AI Assistant for the external audiences (default OFF on the backend).
          // Each only counts with AIAssistant on — read them through isAssistantEnabled.
          AIAssistantCaseManager:
            this.currentTenant.features[
              'Application:AIAssistantCaseManager'
            ]?.toLowerCase() === 'true',
          AIAssistantSpecialist:
            this.currentTenant.features[
              'Application:AIAssistantSpecialist'
            ]?.toLowerCase() === 'true',
          // Per-feature AI switches (host "AI Feature" page). Each only counts
          // with AIService on as well — read them through isAIFeatureEnabled.
          AIDocumentSummary:
            this.currentTenant.features[
              'Application:AIDocumentSummary'
            ]?.toLowerCase() === 'true',
          AIPaperworkPreCheck:
            this.currentTenant.features[
              'Application:AIPaperworkPreCheck'
            ]?.toLowerCase() === 'true',
          AIPaperworkQACheck:
            this.currentTenant.features[
              'Application:AIPaperworkQACheck'
            ]?.toLowerCase() === 'true',
          AIReportQACheck:
            this.currentTenant.features[
              'Application:AIReportQACheck'
            ]?.toLowerCase() === 'true',
          AIBookingAssistant:
            this.currentTenant.features[
              'Application:AIBookingAssistant'
            ]?.toLowerCase() === 'true',
          CompanyEmailCustomization:
            this.currentTenant.features[
              'Application:CompanyEmailCustomization'
            ]?.toLowerCase() === 'true',
          AutoSendingServicePromotionNewsletter:
            this.currentTenant.features[
              'Application:AutoSendingServicePromotionNewsletter'
            ]?.toLowerCase() === 'true',
          AvailabilityCampaignExport:
            this.currentTenant.features[
              'Application:AvailabilityCampaignExport'
            ]?.toLowerCase() === 'true',
        }
        this.features.CaseManagement =
          this.features.MassTorts || this.features.NDIS || this.features.MVA

        // Set edition
        const { editionName } = this.currentTenant
        switch (editionName) {
          case 'MLP':
            this.edition.MLP = true
            break
          case 'LAW CONNECT':
            this.edition.LAW_CONNECT = true
            break
          case 'FUNDING PROVIDER':
            this.edition.FUNDING_PROVIDER = true
            break
          // case 'MLEA':
          //   this.edition.MLEA = true
          //   break
          case 'LMQ PROVIDER':
            this.edition.LMQ_PROVIDER = true
            break
          case 'MARKETING':
            this.edition.MARKETING = true
            break
          default:
            break
        }
      }
      this.setTimeZone(this.currentTenant.timezone ?? moment.tz.guess())
      this.loaded = true

      ApiService.EmailTemplate.getDisabledTemplates().then(res => {
        this.disabledTemplateKeys = res?.data || []
      })

      ApiService.EmailTemplate.getOffshoreVisibleTemplates().then(res => {
        this.offshoreVisibleTemplateKeys = res?.data || []
      })
    })()
    LocalStorageManager.loadUserTableSettings()
  }

  /**
   * Set features from a raw features map (e.g. from anonymous tenant context).
   * featuresMap shape: { 'Application:Assessment': 'True', ... }
   */
  setFeaturesFromMap = (featuresMap: Record<string, string>) => {
    const f = (key: string) => featuresMap[key]?.toLowerCase() === 'true'
    this.features = {
      ...this.features,
      Assessment: f('Application:Assessment'),
      Supplementary: f('Application:Supplementary'),
      FileReview: f('Application:FileReview'),
      MedicalRecord: f('Application:MedicalRecord'),
      MedNeg: f('Application:MedNeg'),
      COTELE: f('Application:COTELE'),
      MarketingCampaign: f('Application:MarketingCampaign'),
      TreatmentProvider: f('Application:TreatmentProvider'),
      MassTorts: f('Application:MassTorts'),
      OT: f('Application:OT'),
      Disbursement: f('Application:Disbursement'),
      DisbursementFunding: f('Application:DisbursementFunding'),
      InsuranceClaim: f('Application:InsuranceClaim'),
      FundingTransaction: f('Application:FundingTransaction'),
      NDIS: f('Application:NDIS'),
      TimeTracking: f('Application:TimeTracking'),
      InquiryCentre: f('Application:InquiryCentre'),
      LegalPracticeAPI: f('Application:LegalPracticeAPI'),
      MVA: f('Application:MVA'),
      LMQ: f('Application:LMQ'),
      ICareClaim: f('Application:ICareClaim'),
      MagExtraResources: f('Application:MagExtraResources'),
      AdHocTemplate: f('Application:AdHocTemplate'),
      TPD: f('Application:TPD'),
      EntraIdLogin: f('Application:EntraIdLogin'),
      AIService: f('Application:AIService'),
      AIAssistant: f('Application:AIAssistant'),
      AIAssistantCaseManager: f('Application:AIAssistantCaseManager'),
      AIAssistantSpecialist: f('Application:AIAssistantSpecialist'),
      AIDocumentSummary: f('Application:AIDocumentSummary'),
      AIPaperworkPreCheck: f('Application:AIPaperworkPreCheck'),
      AIPaperworkQACheck: f('Application:AIPaperworkQACheck'),
      AIReportQACheck: f('Application:AIReportQACheck'),
      AIBookingAssistant: f('Application:AIBookingAssistant'),
      CompanyEmailCustomization: f('Application:CompanyEmailCustomization'),
      AutoSendingServicePromotionNewsletter: f(
        'Application:AutoSendingServicePromotionNewsletter'
      ),
    }
    this.features.CaseManagement =
      this.features.MassTorts || this.features.NDIS || this.features.MVA
  }

  addBusinessDays = (numDays, startDate) => {
    let resultDate = new Date(startDate)
    let businessDaysAdded = 0

    while (numDays > 0) {
      resultDate.setDate(resultDate.getDate() + 1)

      // Check if the current day is a business day (Monday to Friday)
      businessDaysAdded++
      if (resultDate.getDay() !== 0 && resultDate.getDay() !== 6) {
        numDays--
      }
    }
    return businessDaysAdded
  }

  validateTenantAndDomain() {
    let tenantDomain = this.currentTenant?.extraProperties.ClientDomain ?? ''

    if (!tenantDomain.startsWith('https://')) {
      tenantDomain = 'https://' + tenantDomain
    }
    const defaultDomain = process.env.VITE_APP_DOMAIN
    const validDomains = [
      tenantDomain,
      defaultDomain,
      'http://localhost:3000',
    ].filter(i => i)
    if (!validDomains.includes(location.origin)) {
      console.error(
        'Domain is not valid: ' +
          location.origin +
          '. Please check config VITE_APP_DOMAIN'
      )
      authService.logout()
    }
  }
  async getCurrentUser() {
    try {
      const resp = await ApiService.Account.getProfile()

      const settings =
        (await ApiService.SystemConfig.GetSystemConfigForUser())?.data ?? {}
      const user = {
        ...resp.data,
        roles: resp.data.roles.map(i => i.name),
        settings,
      }

      action(() => {
        this.currentUser = user ?? {}
        this.applySessionColourScheme(
          settings[RESOLVED_SESSION_COLOUR_SCHEME_KEY],
          { actsAsCaseManager: false }
        )
        this.authenticated = true
        this.isTenantAdmin = user.roles.includes(ROLE_NAME.TENANT_ADMIN)
        this.isAdminStaff = user.roles.includes(ROLE_NAME.ADMIN_STAFF)
        this.isCaseManager = user.roles.includes(ROLE_NAME.CASE_MANAGER) // test purpose
        this.isSpecialist = user.roles.includes(ROLE_NAME.SPECIALIST)
        this.isStaff = user.roles.includes(ROLE_NAME.STAFF)
        this.isContractor = user.roles.includes(ROLE_NAME.CONTRACTOR)
        this.isAccounting = user.roles.includes(ROLE_NAME.ACCOUNTING)

        this.isAdminOrAccounting = this.isAdminStaff || this.isAccounting
        this.isAdminOrStaff =
          this.isAdminStaff || this.isStaff || this.isAccounting
        this.isAdminOrStaffOrSpecialist =
          this.isAdminStaff ||
          this.isStaff ||
          this.isAccounting ||
          this.isSpecialist
        this.isAdminOrStaffOrCM =
          this.isAdminStaff ||
          this.isStaff ||
          this.isAccounting ||
          this.isCaseManager
        this.isImpersonated = false

        this.isBrokerContractor =
          this.isContractor && user.contractor?.type == CONTRACTOR_TYPE.BROKER

        this.isInsurerContractor =
          this.isContractor &&
          user.contractor?.type != CONTRACTOR_TYPE.BROKER &&
          user.contractorCompany?.businessType == BUSINESS_TYPE.INSURER
      })()
      return resp?.data
    } catch (err) {
      logout()
      return false
    }
  }

  private updateCurrentLoginAccount() {
    const currentLoginAccount = localAccountManager.currentAccount
    currentLoginAccount.tenantName = this.currentTenant.name
    localAccountManager.saveAccount(currentLoginAccount)
  }

  hasFeature(featureName) {
    return this.currentTenant.features[featureName] === 'True'
  }

  isHostAdmin() {
    return this.currentTenant?.id == INITIAL_ID && this.isTenantAdmin
  }

  isHostAdminStaff() {
    return this.currentTenant?.id == INITIAL_ID && this.isAdminStaff
  }

  isHostStaff() {
    return this.currentTenant?.id == INITIAL_ID && this.isStaff
  }

  isBorrower = () => {
    return this.edition.LAW_CONNECT || this.edition.MLP
  }

  checkRolesCurrentUser = (roles = []) => {
    const user = this.currentUser
    const role = user?.roles?.filter(i => roles.indexOf(i) !== -1)
    return !!role?.length
  }

  isEnableServiceFeeEstimation = () => {
    return (
      (
        this.currentTenant?.settings?.['ICS.Feature.ServiceFeeEstimation'] ?? ''
      ).toLowerCase() === 'true'
    )
  }

  /**
   *
   * @param {*} datetime
   * @param {*} mode => start or end
   * @param {*} timezone
   * @returns Datetime UTC string
   */
  convertFilterTimeToUTC(datetime, mode = 'start', timezone?) {
    const currentTimeZone = timezone || dayjs.tz.guess() // this.timezone
    if (datetime) {
      if (mode === 'start') {
        return dayjs(datetime).tz(currentTimeZone).startOf('day').toISOString()
      } else if (mode === 'end') {
        return dayjs(datetime).tz(currentTimeZone).endOf('day').toISOString()
      } else {
        return dayjs(datetime).tz(currentTimeZone).toISOString()
      }
    }

    return null
  }

  formatDate(datetime, timeZone?, format?, skipTimezone = false) {
    const dateFormat = format || this.currentUser?.dateFormat
    const configTimeZone = timeZone || dayjs.tz.guess() // this.timezone
    if (datetime) {
      if (skipTimezone) {
        return dayjs(datetime).format(dateFormat)
      }
      return dayjs(datetime).tz(configTimeZone).format(dateFormat)
    }
    return ''
  }

  formatDateIgnoreTimezone(datetime, format?) {
    const dateFormat = format || this.currentUser?.dateFormat
    if (datetime) {
      return dayjs.utc(datetime).format(dateFormat)
    }
    return ''
  }

  formatDateTime(datetime, timezone?, format?, ignoreDST = false) {
    let dateFormat = format || this.currentUser.dateFormat + ' - HH:mm'

    if (typeof datetime === 'string') {
      return this.getMomentDateTime(datetime, timezone, { ignoreDST })?.format(
        dateFormat
      )
    }

    return this.formatDate(datetime, timezone, dateFormat)
  }

  getMomentDateTime(date, timezoneParam = null, { ignoreDST = false } = {}) {
    const timezoneValue = timezoneParam ? timezoneParam : dayjs.tz.guess() // this.timezone

    if (!date) {
      return date
    }

    if (typeof date === 'string') {
      date = dayjs(date)
    }

    let defaultOffset = dayjs().tz(timezoneValue).utcOffset()

    if (ignoreDST) {
      return date.utcOffset(defaultOffset)
    } else {
      return date.tz(timezoneValue)
    }
  }

  getTodayDate() {
    return dayjs().tz(this.timezone)
  }

  getMomentDate(date, skipTimezone = true) {
    if (!date) {
      return date
    }
    if (skipTimezone) {
      return dayjs(date).startOf('day')
    }
    return dayjs(date).tz(this.timezone).startOf('day')
  }

  /**
   *
   * @param {moment} momentDate
   * @param {string} fromTimeZone. Date created from this TimeZone
   * @param {string} toTimeZone View date at this TimeZone
   * @returns
   */
  getRelativeDate(momentDate: Moment, fromTimeZone, toTimeZone): Moment | null {
    try {
      const fromTimeZoneOffset = momentDate.tz(fromTimeZone).utcOffset()
      const toTimeZoneOffset = momentDate.tz(toTimeZone).utcOffset()

      const shiftOffset = toTimeZoneOffset - fromTimeZoneOffset

      const result = momentDate.utcOffset(toTimeZoneOffset)
      return result
    } catch {
      return null
    }
  }

  //#region Modal
  openConfirmModal(options) {
    this.isOpenConfirmModal = true
    this.confirmModalParams = options
  }
  openWarningExitWithoutSave = ({
    confirmConfig = {},
    discardChange = () => {},
    onSave,
    onCancel,
  }: {
    confirmConfig?: { confirmMessage?: string; saveLabel?: string }
    discardChange: () => void
    onSave: () => void
    onCancel?: () => void
  }) => {
    this.isOpenWarningWithoutSave = true
    this.warningWithoutSaveParams = {
      confirmMessage:
        confirmConfig?.confirmMessage ??
        'Do you want to save changes before opening new section?',
      discardChange,
      onSave,
      onCancel,
      saveLabel: confirmConfig?.saveLabel ?? 'Save',
    }
  }
  closeWarningExitWithoutSave = () => {
    this.isOpenWarningWithoutSave = false
    this.warningWithoutSaveParams = {}
  }
  closeConfirmModal() {
    this.isOpenConfirmModal = false
    this.confirmModalParams = {}
  }

  openPDFViewer(fileId: string, fileName: string) {
    this.isOpenPDFViewer = true
    this.pdfViewerParams = { fileId, fileName }
  }

  closePDFViewer() {
    this.isOpenPDFViewer = false
    this.pdfViewerParams = { fileId: '', fileName: '' }
  }
  //#endregion

  // Confirm message for sending login details, from the server's current login status so staff are
  // warned when the user still could not log in (Account Status Inactive / Login Status Disabled).
  async getResetPasswordConfirmMessage(userId: string) {
    try {
      const res = await ApiService.User.GetLoginStatus(userId)
      return getResetPasswordConfirmMessage(res?.data)
    } catch {
      return getResetPasswordConfirmMessage(null)
    }
  }

  async resendPassword({ userId }: { userId?: string }) {
    if (!userId) {
      return
    }
    this.openConfirmModal({
      message: await this.getResetPasswordConfirmMessage(userId),
      onOk: async () => {
        ApiService.User.ResetPassword(userId)
          .then(async res => {
            if (res) {
              showNotification(
                'Success',
                'Resend login Information successfully!',
                'success'
              )
            }
          })
          .catch(err => {})
      },
    })
  }

  toggleChatbot() {
    this.isOpenChatbot = !this.isOpenChatbot
  }
  openCloseQuickSetting() {
    this.quickSetting = !this.quickSetting
  }

  captureError(message: string) {
    Sentry.captureMessage(message)
  }

  captureException(error: any) {
    Sentry.captureException(error)
  }

  showSuccessMessage = {
    add: () => {
      showNotification(
        'Success',
        'Data has been added successfully',
        'success',
        3
      )
    },
    save: () => {
      showNotification(
        'Success',
        'Data has been saved successfully.',
        'success',
        3
      )
    },
    delete: () => {
      showNotification(
        'Success',
        'Data has been deleted successfully.',
        'success'
      )
    },
    customMessage: (message: string) => {
      showNotification('Success', message, 'success', 3)
    },
  }

  previewEmailTemplateData = {
    isOpen: false,
    fromModule: null,
    fromModuleId: null,
    name: null,
    data: {
      detail: null,
      preview: null,
    },
    mvaClaimId: null,
    params: null,
    isShowEditTemplateTab: true,
    // modalWidth: 1000,
  }

  showPreviewEmailTemplate(data: typeof this.previewEmailTemplateData) {
    this.previewEmailTemplateData = data
  }

  hidePreviewEmailTemplate() {
    this.previewEmailTemplateData.isOpen = false
    this.previewEmailTemplateData.data.detail = null
    this.previewEmailTemplateData.data.preview = null
    this.previewEmailTemplateData.fromModule = null
    this.previewEmailTemplateData.fromModuleId = null
    this.previewEmailTemplateData.name = null
    this.previewEmailTemplateData.params = null
    this.previewEmailTemplateData.mvaClaimId = null
  }

  logActivity(
    fromModule: FROM_MODULE_TYPE,
    objectId: string,
    activityType: ActivityType
  ) {
    notificationService.logActivity(fromModule, objectId, activityType)
  }

  checkSpecialistHadContractDate = async (
    specialist,
    functionCallbackCancel
  ) => {
    if (!specialist) {
      return
    }

    try {
      const res = await ApiService.Specialist.GetListSpecialistContract(
        specialist.id,
        {
          specialistId: specialist.id,
          isActive: true,
        }
      )
      const today = dayjs().startOf('day')
      const contracts = res?.data?.items || []

      // Check if today falls within any contract period
      const hasActiveContract = contracts.some(contract => {
        const startDate = contract.startDate
          ? dayjs(contract.startDate).startOf('day')
          : null
        const endDate = contract.endDate
          ? dayjs(contract.endDate).startOf('day')
          : null

        if (!startDate) {
          return false
        }

        const isAfterStart = today.isSameOrAfter(startDate)
        const isBeforeEnd = !endDate || today.isSameOrBefore(endDate)

        return isAfterStart && isBeforeEnd
      })

      if (!hasActiveContract) {
        if (appStore.isAdminOrStaff) {
          appStore.openConfirmModal({
            message:
              'This specialist does not have a contract with us. Do you want to continue?',
            onOk: () => {},
            functionCallbackCancel: functionCallbackCancel,
          })
        } else {
          functionCallbackCancel()
        }
      }
    } catch (error) {
      // Handle error if needed
    }
  }

  getTimeZoneDisplayName(dateString, timeZone) {
    if (!timeZone || !dateString) {
      return null
    }
    const timezoneInfo = TIMEZONE_LIST.find(i => i.id === timeZone)
    if (!timezoneInfo) {
      return null
    }

    if (this.currentTenant?.defaultCountryId === 16) {
      const timezoneParts = timeZone.split('/')
      const state =
        timezoneParts.length > 1 ? timezoneParts[1] : timezoneParts[0]
      // if country is Australia
      const auTimeZone = AustraliaTimeZones.find(i => i.name === timeZone)

      if (auTimeZone) {
        const isDST = moment(dateString).tz(timeZone)?.isDST()
        return `(${
          isDST ? auTimeZone.daylightTime : auTimeZone.standardTime
        }, ${state})`
      }
    }

    return `(${timezoneInfo.gmt} ${timeZone})`
  }

  setLogoColor = (key = '', value) => {
    if (!key) {
      return
    }

    const color = {
      ...this.colorLogo,
      [key]: value,
    }
    this.logoColor = color
    localStorage.setItem('LOGO_COLOR', JSON.stringify(color))
  }

  initTheme = () => {
    const theme = this.getCurrentTheme()
    this.setTheme(theme)
  }

  setTheme = (type = 'dark') => {
    const root = document.documentElement
    if (!root.style) {
      return
    }

    switch (type) {
      case 'dark':
        root?.style.setProperty('--sidebar-background-color', '#2d2d2d')
        root?.style.setProperty('--sidebar-color-text', '#ffffff')
        break

      case 'light':
        root?.style.setProperty('--sidebar-background-color', '#ffffff')
        root?.style.setProperty('--sidebar-color-text', '#000000')
        break

      case 'dark-blue':
        root?.style.setProperty('--sidebar-background-color', '#233D4D')
        root?.style.setProperty('--sidebar-color-text', '#ffffff')
        break

      default:
        break
    }
    localStorage.setItem('theme', type)
    this.theme = type
  }

  getCurrentDate = () => {
    return this.formatDateIgnoreTimezone(dayjs().toISOString(), 'YYYY-MM-DD')
  }

  getCurrentTheme = () => {
    return localStorage.getItem('theme') || 'dark'
  }

  handleResendEmail = (resendEmail: () => void) => {
    resendEmail()
  }

  isHTML = str => {
    const doc = new DOMParser().parseFromString(str, 'text/html')
    return Array.from(doc.body.childNodes).some(node => node.nodeType === 1)
  }

  /**
   * Check if appointment date falls within specialist's unavailable period
   * @param serviceDate - The date to check
   * @param specialist - The specialist to check against
   * @returns true if date is within unavailable period, false otherwise
   */
  isDateInUnavailablePeriod = (specialist, serviceDate = null) => {
    if (!specialist) {
      return false // if no specialist or date provided, assume available
    }

    if (!specialist.isActive) {
      return true
    }

    if (!specialist?.isNotCurrentlyInterested) {
      return false
    }

    // If serviceDate is null, use today's date
    const dateToCheck = serviceDate ? dayjs(serviceDate) : dayjs()
    const startDate = dayjs(specialist?.notCurrentlyInterestedDate)
    const endDate = specialist?.notCurrentlyInterestedEndDate
      ? dayjs(specialist?.notCurrentlyInterestedEndDate)
      : null

    // Check if appointment is on or after start date
    const isAfterStart = dateToCheck.isSameOrAfter(startDate)

    // Check if appointment is before end date (or no end date set)
    const isBeforeEnd = !endDate || dateToCheck.isSameOrBefore(endDate)

    return isAfterStart && isBeforeEnd
  }

  /**
   * Check if specialist is unavailable and show confirmation modal before executing callback
   * @param specialist - Specialist object to check availability
   * @param callback - Function to execute after confirmation or directly if specialist is available
   */
  checkSpecialistAvailabilityAndExecute = (
    specialist,
    isAllowBookNCI = false,
    callback: () => void
  ) => {
    const msg =
      "Service not available due to primary specialist's unavailability to provide information"
    if (!specialist || !specialist?.isActive) {
      showNotificationWarning(msg)
      return
    }

    if (this.isDateInUnavailablePeriod(specialist)) {
      if (isAllowBookNCI) {
        this.openConfirmModal({
          message:
            'This specialist indicates NCI during this period. Do you want to continue with this booking?',
          onOk: callback,
          functionCallbackCancel: () => {
            return
          },
        })
      } else {
        showNotificationWarning(msg)
        return
      }
    } else {
      callback()
    }
  }
}

const appStore = new AppStore()

export default appStore
